import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ClientSelectionList.css';
import { Client } from '../models/client.model';

interface ClientSelectionListProps {
  clients: Client[];
  selected: Client[];
  onChange: (clients: Client[]) => void;
  filter: string;
  height?: number;                // if omitted, component flex-fills parent
  pagination?: boolean;            // enable classic pagination (default true)
  lazy?: boolean;                  // enable lazy (infinite) loading instead of numbered pages
  pageSizeOptions?: number[];      // selectable page sizes
  defaultPageSize?: number;        // initial page size
  initialLazyBatch?: number;       // first batch size when lazy
  view?: 'table' | 'list';         // presentation variant
}

// Presents clients in a clean, scrollable, multi-select table replacing DataTable inside drawer
const ClientSelectionList: React.FC<ClientSelectionListProps> = ({
  clients,
  selected,
  onChange,
  filter,
  height,
  pagination = true,
  lazy = false,
  pageSizeOptions = [10, 20, 50],
  defaultPageSize = 10,
  initialLazyBatch = 25,
  view = 'table'
}) => {
  const lower = filter.toLowerCase();

  const highlight = (text: any) => {
    if (!text || !lower) return String(text || '');
    const str = String(text);
    const idx = str.toLowerCase().indexOf(lower);
    if (idx === -1) return str;
    const before = str.slice(0, idx);
    const match = str.slice(idx, idx + lower.length);
    const after = str.slice(idx + lower.length);
    return <React.Fragment key={`hl-${Math.random()}`}>{before}<mark className="csl-hl">{match}</mark>{after}</React.Fragment>;
  };
  const filtered = useMemo(() => {
    if (!lower) return clients;
    return clients.filter(c => [c.name, c.description, c.address, (c as any).cityName, c.zipCode, c.code]
      .filter(Boolean)
      .some(val => String(val).toLowerCase().includes(lower)));
  }, [clients, lower]);

  const selectedIds = new Set(selected.map(c => c.id));

  // Pagination state (ignored if lazy)
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1); // 1-based page index

  // Lazy loading state
  const [visibleCount, setVisibleCount] = useState(initialLazyBatch);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  // Reset page when filter changes
  useEffect(() => { setPage(1); setVisibleCount(initialLazyBatch); }, [filter, initialLazyBatch]);

  // IntersectionObserver for lazy loading
  useEffect(() => {
    if (!lazy || pagination) return; // lazy is mutually exclusive with classic pagination
    const target = loaderRef.current;
    if (!target) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          setVisibleCount(v => Math.min(v + pageSize, filtered.length));
        }
      });
    }, { root: target.parentElement, threshold: 0.1 });
    obs.observe(target);
    return () => obs.disconnect();
  }, [lazy, pagination, pageSize, filtered.length]);

  const toggle = (client: Client) => {
    if (!client.id) return;
    if (selectedIds.has(client.id)) {
      onChange(selected.filter(c => c.id !== client.id));
    } else {
      onChange([...selected, client]);
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(c => c.id && selectedIds.has(c.id));
  const someFilteredSelected = filtered.some(c => c.id && selectedIds.has(c.id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      // remove all filtered
      onChange(selected.filter(s => !filtered.find(f => f.id === s.id)));
    } else {
      // add missing filtered
      const missing = filtered.filter(f => f.id && !selectedIds.has(f.id));
      onChange([...selected, ...missing]);
    }
  };

  // Derive rows to show (pagination or lazy or full)
  let rows: Client[] = filtered;
  let totalPages = 1;
  if (!lazy && pagination) {
    totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const start = (page - 1) * pageSize;
    rows = filtered.slice(start, start + pageSize);
  } else if (lazy) {
    rows = filtered.slice(0, visibleCount);
  }

  const goToPage = (p: number) => { if (p >= 1 && p <= totalPages) setPage(p); };

  // LIST VIEW RENDERING
  if (view === 'list') {
    return (
      <div className={`client-select-list-wrapper flex-column ${!height ? 'csl-flex-fill' : ''}`} style={height ? { maxHeight: height, height } : {}}>
        <div className="csl-list-toolbar d-flex align-items-center justify-content-between mb-2 small text-muted flex-shrink-0">
          <div className="d-flex align-items-center gap-2">
            <input
              type="checkbox"
              aria-label="Select all filtered clients"
              checked={allFilteredSelected}
              ref={el => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
              onChange={() => toggleAll()}
            />
            <span>{selected.length} selected</span>
          </div>
          {!lazy && pagination && filtered.length > pageSize && (
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0">Rows</label>
              <select className="form-select form-select-sm" style={{ width: 70 }} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                {pageSizeOptions.map(ps => <option key={ps}>{ps}</option>)}
              </select>
              <div className="d-flex align-items-center gap-1">
                <button type="button" className="btn btn-light btn-sm" disabled={page===1} onClick={()=>goToPage(page-1)}>‹</button>
                <span className="small">{page}/{totalPages}</span>
                <button type="button" className="btn btn-light btn-sm" disabled={page===totalPages} onClick={()=>goToPage(page+1)}>›</button>
              </div>
            </div>
          )}
        </div>
        <ul className="client-select-list list-unstyled mb-0 csl-scroll flex-grow-1">
          {rows.length === 0 && <li className="text-center py-4 small text-muted">No clients found.</li>}
          {rows.map(c => {
            const isSelected = !!(c.id && selectedIds.has(c.id));
            return (
              <li key={c.id || Math.random()} className={`client-item d-flex gap-3 ${isSelected ? 'selected' : ''}`}> 
                <div className="pt-1">
                  <input
                    type="checkbox"
                    aria-label={`Select client ${c.name}`}
                    checked={isSelected}
                    onChange={() => toggle(c)}
                  />
                </div>
                <div className="flex-grow-1" onClick={() => toggle(c)} style={{ cursor:'pointer' }}>
                  <div className="d-flex flex-wrap align-items-baseline gap-2 mb-1">
                    <span className="fw-semibold text-primary client-name-inline">{highlight(c.name)}</span>
                    {c.code && <span className="badge bg-light text-dark border">{highlight(c.code)}</span>}
                    {c.isActive === false ? <span className="badge bg-secondary">Inactive</span> : <span className="badge bg-success">Active</span>}
                  </div>
                  <div className="small text-muted mb-1 line-clamp-2">{highlight(c.description)}</div>
                  <div className="small">
                    <span className="text-muted">Address: </span>{highlight(c.address || '-')}
                    {(c as any).cityName && (
                      <>
                        <span>, </span><span className="text-muted">City: </span>{highlight((c as any).cityName)}
                      </>
                    )}
                    {c.zipCode && (
                      <>
                        <span>, </span><span className="text-muted">Zip: </span>{highlight(c.zipCode)}
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {lazy && visibleCount < filtered.length && (
          <div ref={loaderRef} className="csl-lazy-loader small text-center py-2 text-muted">Loading more… ({visibleCount}/{filtered.length})</div>
        )}
      </div>
    );
  }

  // TABLE VIEW RENDERING
  return (
    <div className={`client-select-table-wrapper flex-column ${!height ? 'csl-flex-fill' : ''}`} style={height ? { maxHeight: height, height } : {}}>
      <div className="csl-table-scroll flex-grow-1">
      <table className="client-select-table table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th style={{ width: '2.5rem' }}>
              <input
                type="checkbox"
                aria-label="Select all filtered clients"
                checked={allFilteredSelected}
                ref={el => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                onChange={toggleAll}
              />
            </th>
            <th>Client</th>
            <th>Description</th>
            <th>Address</th>
            <th>City</th>
            <th>Zip</th>
            <th>Code</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center py-4 small text-muted">No clients found.</td>
            </tr>
          )}
          {rows.map(c => {
            const isSelected = !!(c.id && selectedIds.has(c.id));
            return (
              <tr key={c.id || Math.random()} className={isSelected ? 'selected-row' : ''}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select client ${c.name}`}
                    checked={isSelected}
                    onChange={() => toggle(c)}
                  />
                </td>
                <td className="fw-semibold text-primary client-name-cell" onClick={() => toggle(c)} style={{ cursor: 'pointer' }}>{c.name}</td>
                <td>{c.description}</td>
                <td>{c.address}</td>
                <td>{(c as any).cityName || ''}</td>
                <td>{c.zipCode}</td>
                <td>{c.code || '-'}</td>
                <td>
                  {c.isActive === false ? <span className="badge bg-secondary">Inactive</span> : <span className="badge bg-success">Active</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
  </table>
  </div>
      {/* Pagination Controls */}
      {!lazy && pagination && filtered.length > pageSize && (
        <div className="csl-pagination d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 px-1">
          <div className="d-flex align-items-center gap-2 small text-muted">
            <label className="mb-0">Rows:</label>
            <select className="form-select form-select-sm" style={{ width: '72px' }} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {pageSizeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <span>{filtered.length} total</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <button type="button" className="btn btn-light btn-sm" disabled={page === 1} onClick={() => goToPage(1)} title="First">«</button>
            <button type="button" className="btn btn-light btn-sm" disabled={page === 1} onClick={() => goToPage(page - 1)} title="Prev">‹</button>
            <span className="small px-2">Page {page} / {totalPages}</span>
            <button type="button" className="btn btn-light btn-sm" disabled={page === totalPages} onClick={() => goToPage(page + 1)} title="Next">›</button>
            <button type="button" className="btn btn-light btn-sm" disabled={page === totalPages} onClick={() => goToPage(totalPages)} title="Last">»</button>
          </div>
        </div>
      )}
      {/* Lazy Loader Sentinel */}
      {lazy && visibleCount < filtered.length && (
        <div ref={loaderRef} className="csl-lazy-loader small text-center py-2 text-muted">Loading more… ({visibleCount}/{filtered.length})</div>
      )}
    </div>
  );
};

export default ClientSelectionList;
